import router from '@/router'
import jwtDefaultConfig from './jwtDefaultConfig'

const isTokenExpired = token => {
  if (token === undefined) return true
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(Buffer.from(base64, 'base64')
    .toString('ascii')
    .split('')
    .map(c => `%${(`00${c.charCodeAt(0).toString(16)}`)
      .slice(-2)}`)
    .join(''))
  const { exp } = JSON.parse(jsonPayload)
  const expired = Date.now() >= exp * 1000
  return expired
}

// Function to handle redirect after authentication
const handlePostAuthRedirect = () => {
  const customRoute = sessionStorage.getItem('custom_redirect_after_auth')

  if (customRoute && customRoute !== '/') {
    sessionStorage.removeItem('custom_redirect_after_auth')

    // Try multiple approaches with increasing delays
    const tryRedirect = (attempt = 1) => {
      const delay = attempt * 100 // 100ms, 200ms, 300ms, 400ms, 500ms

      setTimeout(() => {
        if (router.currentRoute.value) {
          // Router is ready, try router.replace
          router.replace(customRoute).catch(() => {
            if (attempt < 5) {
              // Try again with longer delay
              tryRedirect(attempt + 1)
            } else {
              // Final fallback to window.location
              window.location.href = window.location.origin + customRoute
            }
          })
        } else if (attempt < 5) {
          tryRedirect(attempt + 1)
        } else {
          // Router never became ready, use window.location
          window.location.href = window.location.origin + customRoute
        }
      }, delay)
    }

    tryRedirect()
  }
}

// Listen for user authentication events
if (typeof window !== 'undefined') {
  // Listen for vuex-oidc authentication events
  window.addEventListener('vuexoidc:userLoaded', () => {
    handlePostAuthRedirect()
  })

  window.addEventListener('vuexoidc:userSignedIn', () => {
    handlePostAuthRedirect()
  })

  // Fallback: check for route changes after authentication
  if (router) {
    router.afterEach((to, from) => {
      if (to.path === '/' && from.path !== '/') {
        // Small delay to ensure authentication is complete
        setTimeout(() => {
          handlePostAuthRedirect()
        }, 500)
      }
    })
  }
}

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        const token = localStorage.getItem('accessToken')
        const clientErrors = [400, 401, 403, 404]
        const serverErrors = [500, 503]

        // ERROR STATUS: 400, 401, 403, 404
        if (response && clientErrors.includes(response.status) && !isTokenExpired(token)) {
          router.push(`/error#${response.status}`)
        }
        // ERROR STATUS: 500, 503
        if (response && serverErrors.includes(response.status)) {
          router.push(`/error#${response.status}`)
        }

        if (response && response.status === 401 && isTokenExpired(token)) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true

            this.refreshToken().then(r => {
              this.isAlreadyFetchingAccessToken = false

              if (r.data === false) {
                // Store current route in our custom sessionStorage key
                const currentRoute = router.currentRoute.value?.fullPath || window.location.pathname + window.location.search + window.location.hash

                // Use our custom key that won't be overwritten by the OIDC library
                sessionStorage.setItem('custom_redirect_after_auth', currentRoute)

                // Also set the vuex-oidc key for compatibility
                sessionStorage.setItem('vuex_oidc_active_route', currentRoute)

                localStorage.clear()

                // Trigger OIDC authentication
                import('@/store').then(({ default: store }) => {
                  store.dispatch('oidcStore/authenticateOidc')
                }).catch(() => {
                  // Fallback to manual redirect if store import fails
                  window.location.href = '/'
                })
              } else {
                // Update accessToken in localStorage
                this.setToken(r.data.accessToken)
                this.setRefreshToken(r.data.refresh_token)
                this.onAccessTokenFetched(r.data.access_token)
              }
            }).catch(() => {
              this.isAlreadyFetchingAccessToken = false
            })
          }

          const retryOriginalRequest = new Promise(resolve => {
            this.addSubscriber(accessToken => {
              // Make sure to assign accessToken according to your response.
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
          return retryOriginalRequest
        }

        if (response && response.status === 309) {
          router.push(`/${response.data}`)
          return Promise.resolve()
        }

        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }
}
